import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Typography, Button, CssVarsProvider, useTheme } from '@mui/joy';

import InfoIcon from '../../assets/images/Info.svg?react';
import type { SessionStatus } from '../../pages/Crew';
import crewTheme from '../../themes/crew';

interface SessionErrorDisplayProps {
  status: SessionStatus;
  error: string | null;
  isAuthenticated: boolean;
}

const SessionErrorDisplayContent: React.FC<SessionErrorDisplayProps> = ({ status, error, isAuthenticated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  if (status === 'loading' || status === 'enabled') {
    return null;
  }

  const getErrorMessage = (): string => {
    if (error === 'server_error') {
      return t('crew_view.errors.connection_error_message');
    }
    if (error === 'disabled') {
      return t('crew_view.errors.session_disabled');
    }
    return t('crew_view.errors.connection_error_message');
  };

  const handleGoToSessionList = () => {
    navigate('/crew');
  };

  const errorPageBg = theme.palette.background.errorPage || '#F0EFF3';

  return (
    <Box
      className='flex flex-col items-center justify-center'
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        p: 3,
        bgcolor: errorPageBg
      }}
    >
      <Box
        sx={{
          maxWidth: 400,
          width: '392px',
          height: '176px',
          p: '20px',
          borderRadius: 'md',
          bgcolor: 'background.surface', // White card background (#fbfcfe)
          boxShadow: 'sm'
        }}
      >
        <Box className='flex flex-col'>
          <Box className='flex flex-row items-center gap-1 mb-3'>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boder: 0
              }}
            >
              <InfoIcon className='w-4 h-4' fill={theme.palette.danger.plainColor} />
            </Box>

            <Typography
              level='title-lg'
              sx={{
                color: 'danger.plainColor',
                lineHeight: 1.66
              }}
            >
              {status === 'disabled'
                ? t('crew_view.errors.session_disabled_status')
                : t('crew_view.errors.connection_error_status')}
            </Typography>
          </Box>

          <Typography
            level='body-sm'
            sx={{
              color: 'neutral.outlinedColor',
              mb: 3
            }}
          >
            {getErrorMessage()}
          </Typography>

          {isAuthenticated && (
            <Box className='flex justify-end w-full h-full'>
              <Button
                variant='solid'
                color='neutral'
                component='a'
                href='/crew'
                sx={{
                  borderRadius: 'sm'
                }}
              >
                <Typography level='body-md' sx={{ fontFamily: 'Be Vietnam Pro', color: 'neutral.solidColor' }}>
                  {t('crew_view.errors.go_to_session_list')}
                </Typography>
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const SessionErrorDisplay: React.FC<SessionErrorDisplayProps> = (props) => {
  return (
    <CssVarsProvider theme={crewTheme} disableTransitionOnChange>
      <SessionErrorDisplayContent {...props} />
    </CssVarsProvider>
  );
};

export default SessionErrorDisplay;
